import type { DimCatalogRow } from '~/api/endpoints/catalog'
import { CAMPAIGN_EXACT_DIM } from '~/state/campaignPredicate'

/** FU-03: pin-replay driver-arm filter strip. Live driver tiles prune filters to the driver
 *  fact's dims before the walk fires (components/ReviewPage/stripToFactDims.ts — the filters
 *  half of this rule, kept byte-compatible); pin replay + export share THIS helper so chart and
 *  xlsx cannot diverge. Consumers: ChartFromPin's two driver arms (via useDriverDims),
 *  fetchPinData.runDriver's callers, pinToSheet metadata (via WalkSlice.effective/dropped). */

/** The driver fact's dim set, or null while the catalog is unresolved (pending OR errored) —
 *  callers treat null as "fire unstripped" (pre-FU-03 behavior, visible degradation). */
export function driverDimsOf(
  rows: DimCatalogRow[] | undefined,
  driverFact: string,
): Set<string> | null {
  if (!rows) return null
  return new Set(rows.filter((r) => r.fact_table === driverFact).map((r) => r.dim_name))
}

export interface DriverArmFilters {
  /** The fact-valid subset of the frozen filters — what the driver walk fires. */
  filters: Record<string, string[]>
  /** Frozen keys WITH VALUES the driver fact cannot carry (empty-valued keys are no-ops). */
  dropped: string[]
  /** The exact Campaign dim would be dropped: firing the stripped set would silently WIDEN a
   *  campaign-scoped arm to unfiltered totals — callers must block the walk loudly instead
   *  (spec decision 2). */
  campaignBlocked: boolean
}

export function resolveDriverArmFilters(
  frozen: Record<string, string[]>,
  driverDims: Set<string>,
): DriverArmFilters {
  const filters: Record<string, string[]> = {}
  const dropped: string[] = []
  for (const [k, v] of Object.entries(frozen)) {
    if (driverDims.has(k)) filters[k] = v
    else if (v.length > 0) dropped.push(k)
  }
  return { filters, dropped, campaignBlocked: dropped.includes(CAMPAIGN_EXACT_DIM) }
}
